import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import BrandMark from '@/components/BrandMark';

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigationRef = useRef(null);
  const menuRef = useRef(null);
  const menuToggleRef = useRef(null);
  const location = useLocation();
  const { showRsvp, showRegistry } = useSettings();
  const isHome = location.pathname === '/';
  const isOverlay = isHome && !isScrolled && !isMobileMenuOpen;

  // Define all nav items
  const allNavItems = [
    { label: 'Home', path: '/', alwaysShow: true },
    { label: 'Schedule', path: '/schedule', alwaysShow: true },
    { label: 'Travel', path: '/travel', alwaysShow: true },
    { label: 'Registry', path: '/registry', alwaysShow: false, requiresRegistry: true },
    { label: 'FAQs', path: '/faqs', alwaysShow: true },
    { label: 'RSVP', path: '/rsvp', alwaysShow: false, requiresRsvp: true },
  ];

  // Filter nav items based on settings
  const navItems = allNavItems.filter(item => {
    if (item.alwaysShow) return true;
    if (item.requiresRegistry && showRegistry) return true;
    if (item.requiresRsvp && showRsvp) return true;
    return false;
  });

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 36);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep the page fixed behind the menu and contain keyboard focus within it.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const backgroundElements = [document.querySelector('main'), document.querySelector('footer')]
      .filter(Boolean)
      .map((element) => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden'),
      }));

    document.body.style.overflow = 'hidden';
    backgroundElements.forEach(({ element }) => {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    });

    const focusFirstLink = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector('a[href]')?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMobileMenuOpen(false);
        window.requestAnimationFrame(() => menuToggleRef.current?.focus());
        return;
      }

      if (event.key !== 'Tab' || !navigationRef.current) return;

      const focusableElements = Array.from(
        navigationRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const focusIsInsideMenu = focusableElements.includes(document.activeElement);

      if (event.shiftKey && (!focusIsInsideMenu || document.activeElement === firstElement)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (!focusIsInsideMenu || document.activeElement === lastElement)) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFirstLink);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      backgroundElements.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', ariaHidden);
        }
      });
    };
  }, [isMobileMenuOpen]);

  return (
    <nav
      ref={navigationRef}
      data-testid="main-navigation"
      role={isMobileMenuOpen ? 'dialog' : undefined}
      aria-modal={isMobileMenuOpen ? 'true' : undefined}
      aria-label={isMobileMenuOpen ? 'Site navigation' : 'Primary navigation'}
      className={`fixed top-0 left-0 right-0 z-[110] transition-all duration-300 ${
        isOverlay
          ? 'home-overlay-navigation py-4 md:py-5 bg-transparent text-linen'
          : 'py-3 bg-linen/95 text-forest border-b border-forest/10 shadow-[0_4px_25px_rgba(51,59,41,0.04)] backdrop-blur-md'
      }`}
    >
        <div className="max-w-[90rem] mx-auto px-5 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="relative z-[111] block w-36 md:w-44"
          >
            <BrandMark inverse={isOverlay} className="block w-full h-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-7 xl:gap-9">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`relative text-[0.82rem] uppercase tracking-[0.14em] font-semibold transition-colors duration-300 group ${
                  isOverlay
                    ? location.pathname === item.path ? 'text-linen' : 'text-linen/80 hover:text-linen'
                    : location.pathname === item.path ? 'text-forest' : 'text-forest/70 hover:text-forest'
                }`}
              >
                {item.label}
                <span className={`absolute left-0 -bottom-1 h-px transition-all duration-300 ${
                  isOverlay ? 'bg-linen' : 'bg-forest'
                } ${
                  location.pathname === item.path
                    ? 'w-full'
                    : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={menuToggleRef}
            data-testid="mobile-menu-toggle"
            className="relative z-[111] lg:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
          >
            <span className={`w-7 h-px transition-all duration-300 ${isOverlay ? 'bg-linen' : 'bg-forest'} ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`w-7 h-px transition-opacity duration-300 ${isOverlay ? 'bg-linen' : 'bg-forest'} ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`w-7 h-px transition-all duration-300 ${isOverlay ? 'bg-linen' : 'bg-forest'} ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            ref={menuRef}
            id="mobile-navigation-menu"
            data-testid="mobile-menu"
            className="mobile-menu"
          >
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
    </nav>
  );
};

export default Navigation;
